import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { trappingZoneResultsCounts } from '../../helpers/TrappingUtils';

const SeasonalOverviewReportGraph = (props) => {
  const graphRef = React.useRef();
  const [width, setWidth] = useState({ width: null, modifier: 'initial' });

  useEffect(() => {
    const handleBeforePrint = () => {
      setWidth({ width: 940, modifier: 'beforeprint' });
    };
    window.addEventListener('beforeprint', handleBeforePrint);
    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
    };
  }, []);

  useEffect(() => {
    const handleAfterPrint = () => {
      setWidth({
        width: graphRef.current.container.current.offsetWidth,
        modifier: 'afterprint'
      });
    };
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, []);

  // We only want to set the graph width to null when the width
  // modified by 'afterprint'. This allows the graph to responsive after
  // the print events.
  useEffect(() => {
    if (width.modifier !== 'afterprint') return;
    setWidth({ width: null, modifier: 'postafterprint' });
  }, [width.modifier]);

  return (
    <HighchartsReact
      ref={graphRef}
      highcharts={Highcharts}
      options={{
        chart: {
          type: 'column',
          height: 340,
          width: width.width
        },
        credits: {
          enabled: false
        },
        title: {
          text: ''
        },
        xAxis: {
          categories: props.selectedProperties.map((property) => property.name)
        },
        yAxis: {
          title: {
            text: 'Thresholds'
          }
        },
        legend: {
          enabled: false
        },
        tooltip: {
          formatter: function () {
            return (
              '<b>' +
              this.series.userOptions.stack +
              '</b><br/>' +
              this.series.name +
              ': ' +
              this.y +
              '<br/>' +
              'Total: ' +
              this.point.stackTotal
            );
          }
        },
        plotOptions: {
          column: {
            stacking: 'normal',
            groupPadding: 0,
            pointPadding: 0,
            centerInCategory: true
          }
        },
        series: props.selectedProperties
          .flatMap((property, propertyIndex) => {
            return property.trapping_zones.flatMap((trappingZone) => {
              const trappingZoneResults =
                props.trappingResults[trappingZone.id];

              if (trappingZoneResults) {
                const counts = trappingZoneResultsCounts(
                  props.selectedInsectGroup,
                  trappingZoneResults,
                  trappingZone
                );

                const actionableThresholdsData = [];
                const coveredThresholdsData = [];
                for (let i = 0; i < propertyIndex; i++) {
                  actionableThresholdsData.push(null);
                  coveredThresholdsData.push(null);
                }

                actionableThresholdsData.push(counts.thresholdMetRecords);
                coveredThresholdsData.push(counts.coveredThresholdRecords);

                return [
                  {
                    name: `Actionable Thresholds`,
                    data: actionableThresholdsData,
                    stack: `${property.identifier} ${property.name} - ${trappingZone.name}`,
                    color: '#DC3545'
                  },
                  {
                    name: `Covered Thresholds`,
                    data: coveredThresholdsData,
                    stack: `${property.identifier} ${property.name} - ${trappingZone.name}`,
                    color: '#17A2B8'
                  }
                ];
              }
            });
          })
          .filter((x) => x !== undefined)
      }}
    />
  );
};

SeasonalOverviewReportGraph.propTypes = {
  selectedProperties: PropTypes.array.isRequired,
  selectedInsectGroup: PropTypes.object.isRequired,
  trappingResults: PropTypes.object.isRequired
};

export default observer(SeasonalOverviewReportGraph);
